class BuildYourBundle extends HTMLElement {
  constructor() {
    super();

    this._selectors = {
      quickAddSelectorBundle: '[js-quick-add-bundle]',
      quickAddBundleAtc: '[js-add-bundle-to-cart]',
      bundleConfig: '[js-bundle-config]',
      bundleCount: '[js-bundle-count]',
      bundleOriginalPrice: '[js-bundle-original-price]',
      bundleMidPrice: '[js-bundle-mid-price]',
      bundleDiscountedPrice: '[js-bundle-discounted-price]',
      bundlePlaceholder: '[js-bundle-placeholder]',
      progressStep: '[js-bundle-progress-step]',
      stepStatus: '[js-bundle-step-status]',
      stepStatusLabel: '[js-bundle-step-status-label]',
      addLabel: '[js-bundle-add-label]',
      bundleAtcLabel: '[js-bundle-atc-label]',
      bundleTierMessage: '[js-bundle-tier-message]',
      accordionItem: '[js-accordion-item]',
      productCardBundle: '[js-product-card-bundle]',
      bundleRemoveSelection: '[js-bundle-remove-selection]',
      bundleAtcStatus: '[js-bundle-atc-status]',
      bundleSelectionCrawl: '[js-bundle-selection-crawl]',
      accordionHeader: '[js-accordion-header]',
      bundleSticky: '[js-bundle-sticky]',
      bundleStickyToggle: '[js-bundle-sticky-toggle]',
      stickySentinel: '.section-byb__atc-wrap',
    };

    this._stepConfig = [];
    this._discountPercent = 0;
    // Button labels: "Start Building" while empty, the ATC label once building.
    this._atcLabel = (this.dataset.atcLabel || 'Add bundle to cart').trim();
    this._startLabel = (this.dataset.startLabel || 'Start Building').trim();
    // Clicking a filled preview slot jumps to that product (section setting).
    this._slotClickEnabled = this.dataset.slotClick !== 'false';
    // Shareable URL tokens: write product handles or variant ids (section setting).
    this._presetKey = this.dataset.presetKey === 'id' ? 'id' : 'handle';
    // Preset link opened with an incomplete bundle: jump to the first unfilled step?
    this._presetIncompleteScroll = this.dataset.presetIncomplete !== 'stay';
    this._presetApplied = false;
    // Discount tiers + additional sitewide promo (visual only).
    this._tier1Min = parseInt(this.dataset.tier1Min, 10) || 3;
    this._tier2Enabled = this.dataset.tier2 === 'true';
    this._tier2Min = parseInt(this.dataset.tier2Min, 10) || 4;
    this._tier2Discount = parseFloat(this.dataset.tier2Discount) || 0;
    this._sitewideEnabled = this.dataset.sitewide === 'true';
    this._sitewideDiscount = parseFloat(this.dataset.sitewideDiscount) || 0;
    this._selections = new Map();
    this._firedBuilderComplete = false;
    this._prevStepAdvanceFlags = null;
    this._optionalStepRegressed = new Set();
    this._stickyBar = null;
    this._stickyObserver = null;
  }

  connectedCallback() {
    this._parseConfig();
    this._initSelectionState();
    this._applyPreset();

    const quickAddButtons = this.querySelectorAll(this._selectors.quickAddSelectorBundle);
    quickAddButtons.forEach((button) => {
      button.addEventListener('click', (evt) => {
        this._handleBundleProductAdd(evt);
      });
    });

    this.addEventListener('click', (evt) => {
      this._onBundleRemoveClick(evt);
      const quickAdd = evt.target.closest(this._selectors.quickAddSelectorBundle);
      const removeBtn = evt.target.closest(this._selectors.bundleRemoveSelection);
      if (!quickAdd && !removeBtn) {
        const pdpLink = evt.target.closest('a[href]');
        if (pdpLink && pdpLink.closest(this._selectors.productCardBundle)) {
          evt.preventDefault();
        }
        const filledPlaceholder = evt.target.closest(`${this._selectors.bundlePlaceholder}.has-product`);
        if (filledPlaceholder) {
          this._scrollToPlaceholderProduct(filledPlaceholder);
        }
      }
      if (evt.target.closest(this._selectors.accordionHeader)) {
        requestAnimationFrame(() => this._syncProgress());
      }
    });

    this.addEventListener(
      'keydown',
      (evt) => {
        if (evt.key !== 'Enter' && evt.key !== ' ') return;
        const quickAdd = evt.target.closest(this._selectors.quickAddSelectorBundle);
        const removeBtn = evt.target.closest(this._selectors.bundleRemoveSelection);
        if (quickAdd || removeBtn) return;
        const filledPlaceholder = evt.target.closest(`${this._selectors.bundlePlaceholder}.has-product`);
        if (filledPlaceholder) {
          evt.preventDefault();
          this._scrollToPlaceholderProduct(filledPlaceholder);
          return;
        }
        const pdpLink = evt.target.closest('a[href]');
        if (pdpLink && pdpLink.closest(this._selectors.productCardBundle)) {
          evt.preventDefault();
        }
      },
      true
    );

    this.querySelectorAll(this._selectors.quickAddBundleAtc).forEach((bundleAtc) => {
      bundleAtc.addEventListener('click', (evt) => {
        this._handleBundleAddToCart(evt);
      });
    });

    this._initStickyBar();

    this._syncBundle();

    // Preset link with an incomplete bundle: open the first unfilled required
    // step so the visitor lands right where they can finish it (section setting).
    if (this._presetApplied && this._presetIncompleteScroll) {
      const unfilled = this._stepConfig.find(
        (step) => step.mandatory && !this._isStepRequirementMet(step)
      );
      if (unfilled) {
        this._expandBundleAccordion(unfilled.blockId, { closeSiblings: false });
        this._scrollBundleStepProductsIntoView(unfilled.blockId);
      }
    }
  }

  /* Config */

  _parseConfig() {
    const configEl = this.querySelector(this._selectors.bundleConfig);
    if (!configEl) return;

    try {
      const parsedConfig = JSON.parse(configEl.textContent || '{}');
      this._discountPercent = parseFloat(parsedConfig.discountPercent) || 0;
      this._stepConfig = (parsedConfig.steps || []).map((jsonStep) => ({
        blockId: String(jsonStep.blockId),
        selectionSize: parseInt(jsonStep.selectionSize, 10) || 0,
        mandatory: jsonStep.mandatory !== false,
      }));
    } catch (error) {
      console.error('BuildYourBundle: invalid bundle config', error);
    }
  }

  _initSelectionState() {
    this._stepConfig.forEach((step) => {
      if (!this._selections.has(step.blockId)) {
        this._selections.set(step.blockId, []);
      }
    });
  }

  /* Steps: requirements & progress chain */

  _getStepSelectedCount(step) {
    return (this._selections.get(step.blockId) || []).length;
  }

  _isStepRequirementMet(step) {
    if (!step.mandatory) return true;
    return this._getStepSelectedCount(step) >= step.selectionSize;
  }

  _isStepVisualComplete(step) {
    const count = this._getStepSelectedCount(step);
    if (!step.mandatory) {
      return count > 0;
    }
    return count >= step.selectionSize;
  }

  _trackOptionalStepRegression(step, prevCount, nextCount) {
    if (!step || step.mandatory) return;
    const id = String(step.blockId);
    if (prevCount > 0 && nextCount === 0) {
      this._optionalStepRegressed.add(id);
    } else if (nextCount > 0) {
      this._optionalStepRegressed.delete(id);
    }
  }

  _stepPassesForProgress(step) {
    if (step.mandatory) {
      return this._isStepRequirementMet(step);
    }
    const count = this._getStepSelectedCount(step);
    if (count > 0) return true;
    return !this._optionalStepRegressed.has(String(step.blockId));
  }

  _stepsBeforeIndexAllPass(stepIndex) {
    for (let i = 0; i < stepIndex; i++) {
      if (!this._stepPassesForProgress(this._stepConfig[i])) return false;
    }
    return true;
  }

  /* Quick add */

  _handleBundleProductAdd(evt) {
    evt.preventDefault();
    const button = evt.currentTarget;

    if (button.disabled) return;

    const blockId = button.dataset.bundleBlockId;
    if (!blockId) return;

    const step = this._stepConfig.find((configStep) => configStep.blockId === blockId);
    if (!step || step.selectionSize < 1) return;

    const variantId = button.dataset.variantId;
    const variantPrice = parseInt(button.dataset.variantPrice || '0', 10);
    const productTitle = button.dataset.productTitle || '';
    const productCard = button.closest(this._selectors.productCardBundle);

    if (!variantId) return;

    const prevCount = (this._selections.get(blockId) || []).length;
    let selections = [...(this._selections.get(blockId) || [])];
    const existingIndex = selections.findIndex((selection) => selection.variantId === variantId);

    if (existingIndex >= 0) {
      selections = selections.filter((_selection, selectionIndex) => selectionIndex !== existingIndex);
    } else {
      if (selections.length >= step.selectionSize) {
        selections = selections.slice(0, -1);
      }
      selections.push(this._selectionFromButton(button));
    }

    this._trackOptionalStepRegression(step, prevCount, selections.length);
    this._selections.set(blockId, selections);
    this._expandBundleAccordion(blockId, { closeSiblings: false });
    this._syncBundle();
  }

  _selectionFromButton(button) {
    return {
      variantId: button.dataset.variantId,
      priceCents: parseInt(button.dataset.variantPrice || '0', 10),
      title: button.dataset.productTitle || '',
      card: button.closest(this._selectors.productCardBundle),
      productUrl: button.dataset.productUrl || '',
      bundleImage: button.dataset.bundleImage || '',
    };
  }

  /* --- URL preset (shareable deep-link): read on load, write on change --- */

  _handleFromUrl(url) {
    if (!url) return '';
    const match = String(url).match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  _findAddButton(blockId, key) {
    // The key is either a product handle or a variant id — accept both.
    const buttons = this.querySelectorAll(
      `${this._selectors.quickAddSelectorBundle}[data-bundle-block-id="${blockId}"]`
    );
    return Array.from(buttons).find(
      (btn) =>
        btn.dataset.variantId === key || this._handleFromUrl(btn.dataset.productUrl) === key
    );
  }

  _parsePresetTokens() {
    // Format: ?b-preset=p1_key&p2_key...  key = product handle or variant id;
    // the legacy "bundle-preset" marker and a "+idx" suffix are tolerated.
    const search = window.location.search;
    let marker = 'b-preset=';
    let at = search.indexOf(marker);
    if (at === -1) {
      marker = 'bundle-preset=';
      at = search.indexOf(marker);
    }
    if (at === -1) return [];
    return search
      .slice(at + marker.length)
      .replace(/%2B/gi, '+') // tolerate mail clients that percent-encode the plus
      .split('&')
      .map((token) => token.match(/^p(\d+)_(.+?)(?:\+\d+)?$/))
      .filter(Boolean)
      .map((m) => ({ stepNum: parseInt(m[1], 10), key: m[2] }));
  }

  _applyPreset() {
    const tokens = this._parsePresetTokens();
    if (!tokens.length) return;
    this._presetApplied = true;
    tokens.forEach(({ stepNum, key }) => {
      const step = this._stepConfig[stepNum - 1];
      if (!step) return;
      const button = this._findAddButton(step.blockId, key);
      // Skip sold-out products (their add button renders disabled).
      if (!button || button.disabled || !button.dataset.variantId) return;
      const selections = this._selections.get(step.blockId) || [];
      if (selections.some((s) => s.variantId === button.dataset.variantId)) return;
      if (selections.length >= step.selectionSize) return;
      selections.push(this._selectionFromButton(button));
      this._selections.set(step.blockId, selections);
    });
  }

  _writeUrl() {
    const tokens = [];
    this._stepConfig.forEach((step, stepIndex) => {
      (this._selections.get(step.blockId) || []).forEach((selection) => {
        const key =
          this._presetKey === 'id' ? selection.variantId : this._handleFromUrl(selection.productUrl);
        if (key) tokens.push(`p${stepIndex + 1}_${key}`);
      });
    });
    // Keep any unrelated params; drop the previous preset (both markers plus the
    // bare pN_ tokens URLSearchParams splits out), then append the fresh preset last.
    const params = new URLSearchParams(window.location.search);
    params.delete('b-preset');
    params.delete('bundle-preset');
    [...params.keys()].forEach((key) => {
      if (/^p\d+_/.test(key)) params.delete(key);
    });
    const base = params.toString();
    const preset = tokens.length ? `b-preset=${tokens.join('&')}` : '';
    const query = [base, preset].filter(Boolean).join('&');
    const newUrl = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
    const currentUrl = window.location.pathname + window.location.search + window.location.hash;
    if (newUrl === currentUrl) return;
    window.history.replaceState({}, '', newUrl);
  }

  /* Cart */

  _getCart() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  getSectionsToRender() {
    const cart = this._getCart();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      return cart.getSectionsToRender();
    }
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
      },
      {
        id: 'cart-icon-qty',
        section: 'cart-icon-qty',
      },
    ];
  }

  _getTierDiscountRatio() {
    // Base (tier 1) percent, bumped to tier 2 once its item threshold is met.
    const count = this._getTotalSelectedCount();
    let pct = this._discountPercent;
    if (this._tier2Enabled && this._tier2Discount > 0 && count >= this._tier2Min) {
      pct = this._tier2Discount;
    }
    return Math.min(Math.max(pct, 0), 100) / 100;
  }

  _getSitewideRatio() {
    if (!this._sitewideEnabled || this._sitewideDiscount <= 0) return 0;
    return Math.min(Math.max(this._sitewideDiscount, 0), 100) / 100;
  }

  _getBundleDiscountRatio() {
    // Bundle (tier) discount, then the additional sitewide promo stacked on the
    // already-discounted price: 1 - (1 - tier) * (1 - sitewide). Visual only.
    const tier = this._getTierDiscountRatio();
    const sitewide = this._getSitewideRatio();
    return 1 - (1 - tier) * (1 - sitewide);
  }

  _updateTierMessage() {
    const el = this.querySelector(this._selectors.bundleTierMessage);
    if (!el) return;
    const count = this._getTotalSelectedCount();

    let template = '';
    let remaining = 0;
    let discount = this._discountPercent;
    if (count < this._tier1Min) {
      template = el.dataset.msgTier1 || '';
      remaining = Math.max(this._tier1Min - count, 0);
      discount = this._discountPercent;
    } else if (count < this._tier2Min) {
      template = el.dataset.msgTier2 || '';
      remaining = Math.max(this._tier2Min - count, 0);
      discount = this._tier2Discount;
    } else {
      template = el.dataset.msgDone || '';
      remaining = 0;
      discount = this._tier2Discount;
    }

    el.textContent = template
      .replace(/\[count\]/g, String(remaining))
      .replace(/\[discount\]/g, String(discount));
  }

  _getBundleCartItems() {
    const bundleGroupId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `bg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 11)}`;

    const discountRatio = this._getBundleDiscountRatio();
    const lineMap = new Map();

    this._stepConfig.forEach((step) => {
      const selections = this._selections.get(step.blockId) || [];
      selections.forEach((selection) => {
        const id = parseInt(selection.variantId, 10);
        if (isNaN(id)) return;
        const origCents = selection.priceCents || 0;
        const discountedUnit = Math.round(origCents * (1 - discountRatio));
        if (!lineMap.has(id)) {
          lineMap.set(id, { qty: 0, sumOrig: 0, sumDiscounted: 0 });
        }
        const entry = lineMap.get(id);
        entry.qty += 1;
        entry.sumOrig += origCents;
        entry.sumDiscounted += discountedUnit;
      });
    });

    return [...lineMap.entries()].map(([variantId, data]) => {
      const q = data.qty;
      const unitDiscounted = q > 0 ? Math.round(data.sumDiscounted / q) : 0;
      const unitOrig = q > 0 ? Math.round(data.sumOrig / q) : 0;
      return {
        id: variantId,
        quantity: q,
        properties: {
          _bundle_builder: 'true',
          _bundle_group_id: bundleGroupId,
          _bundle_unit_price_cents: String(unitDiscounted),
          _bundle_unit_compare_cents: String(unitOrig),
        },
      };
    });
  }

  _getBuilderEstimatedTotalCents() {
    let totalCents = 0;
    this._selections.forEach((selections) => {
      selections.forEach((selection) => {
        totalCents += selection.priceCents;
      });
    });
    const discountRatio = this._getBundleDiscountRatio();
    return Math.round(totalCents * (1 - discountRatio));
  }

  _allocateBundleCentsFromCart(cartItems, requestedItems) {
    const need = new Map();
    requestedItems.forEach((lineItem) => {
      const variantId = lineItem.id;
      need.set(variantId, (need.get(variantId) || 0) + lineItem.quantity);
    });
    let cents = 0;
    (cartItems || []).forEach((cartLine) => {
      const variantId = cartLine.variant_id;
      let remaining = need.get(variantId) || 0;
      if (remaining <= 0) return;
      const take = Math.min(remaining, cartLine.quantity);
      const perUnit = cartLine.quantity > 0 ? cartLine.final_line_price / cartLine.quantity : 0;
      cents += Math.round(perUnit * take);
      need.set(variantId, remaining - take);
    });
    return cents;
  }

  /* Crawlable summary & dataLayer */

  _syncCrawlableSelectionSummary() {
    const host = this.querySelector(this._selectors.bundleSelectionCrawl);
    if (!host) return;
    host.innerHTML = '';
    const orderedSelections = [];
    this._stepConfig.forEach((step) => {
      const selections = this._selections.get(step.blockId) || [];
      selections.forEach((selection) => orderedSelections.push(selection));
    });
    if (!orderedSelections.length) {
      const emptyMessage = document.createElement('p');
      emptyMessage.textContent = 'Current bundle selection: none.';
      host.appendChild(emptyMessage);
      return;
    }
    const intro = document.createElement('p');
    intro.textContent = 'Current bundle selection (updates as you build):';
    host.appendChild(intro);
    const orderedList = document.createElement('ol');
    orderedSelections.forEach((selection) => {
      const listItem = document.createElement('li');
      const productUrl = selection.productUrl || '';
      if (productUrl) {
        const link = document.createElement('a');
        link.href = productUrl;
        link.textContent = selection.title || 'Product';
        listItem.appendChild(link);
      } else {
        listItem.textContent = selection.title || 'Product';
      }
      orderedList.appendChild(listItem);
    });
    host.appendChild(orderedList);
  }

  _publishBundleBuilderComplete() {
    const allComplete = this._stepsCompleteFlags().every(Boolean);
    if (!allComplete) {
      this._firedBuilderComplete = false;
      return;
    }
    if (this._firedBuilderComplete) return;
    this._firedBuilderComplete = true;
    window.dataLayer = window.dataLayer || [];
    let itemSlotsFilled = 0;
    this._selections.forEach((selections) => {
      itemSlotsFilled += selections.length;
    });
    window.dataLayer.push({
      event: 'bundle_builder_complete',
      bundle_discount_percent_shown: this._discountPercent,
      bundle_item_slots_filled: itemSlotsFilled,
    });
  }

  /* Bundle ATC UI */

  _getBundleAtcStatusEls() {
    return this.querySelectorAll(this._selectors.bundleAtcStatus);
  }

  _clearBundleAtcMessage() {
    this._getBundleAtcStatusEls().forEach((statusEl) => {
      statusEl.textContent = '';
      statusEl.classList.add('hidden');
      statusEl.classList.remove('section-byb__atc-message--error');
      statusEl.setAttribute('aria-live', 'polite');
    });
  }

  _setBundleAtcError(message) {
    const text = message || window.cartStrings?.error || 'Something went wrong. Please try again.';
    this._getBundleAtcStatusEls().forEach((statusEl) => {
      statusEl.textContent = text;
      statusEl.classList.remove('hidden');
      statusEl.classList.add('section-byb__atc-message--error');
      statusEl.setAttribute('aria-live', 'assertive');
    });
  }

  _publishBundleAddToDataLayer(items, cartPayload) {
    window.dataLayer = window.dataLayer || [];
    const variantIds = items.map((lineItem) => String(lineItem.id));
    const lineCount = items.reduce((sum, lineItem) => sum + (lineItem.quantity || 0), 0);
    const allocatedLineCents =
      cartPayload && cartPayload.items
        ? this._allocateBundleCentsFromCart(cartPayload.items, items)
        : undefined;
    const builderEstimateCents = this._getBuilderEstimatedTotalCents();
    window.dataLayer.push({
      event: 'bundle_add_to_cart',
      bundle_variant_ids: variantIds,
      bundle_distinct_variants: variantIds.length,
      bundle_line_quantity: lineCount,
      bundle_discount_percent_shown: this._discountPercent,
      cart_item_count: typeof cartPayload.item_count === 'number' ? cartPayload.item_count : undefined,
      bundle_builder_estimate_cents: builderEstimateCents,
      bundle_cart_allocated_line_cents: typeof allocatedLineCents === 'number' ? allocatedLineCents : undefined,
      bundle_pricing_matches_estimate:
        typeof allocatedLineCents === 'number'
          ? Math.abs(allocatedLineCents - builderEstimateCents) <= 2
          : undefined,
    });
  }

  _handleBundleAddToCart(evt) {
    evt.preventDefault();
    const bundleAtc = evt.currentTarget;

    // Start Building state: no items yet — jump to the first step instead of adding.
    if (this._isStartBuildingMode()) {
      this._goToFirstStep();
      return;
    }

    if (bundleAtc.disabled || bundleAtc.getAttribute('aria-disabled') === 'true') return;

    const items = this._getBundleCartItems();
    if (!items.length) return;

    this._clearBundleAtcMessage();
    this.cart = this._getCart();
    bundleAtc.classList.add('loading');
    bundleAtc.setAttribute('aria-disabled', 'true');
    const spinner = bundleAtc.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');

    const data = {
      items,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    };

    fetch(`${window.Shopify.routes.root}cart/add.js`, {
      ...fetchConfig(),
      body: JSON.stringify(data),
    })
      .then((fetchResponse) => fetchResponse.json())
      .then((cartResponse) => {
        if (cartResponse.status) {
          const errorMessage =
            cartResponse.message ||
            (cartResponse.description && String(cartResponse.description)) ||
            (cartResponse.errors && String(cartResponse.errors));
          this._setBundleAtcError(errorMessage);
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'build-your-bundle',
            message: cartResponse.message,
            errors: cartResponse.errors || cartResponse.description,
          });
          return;
        }

        this._clearBundleAtcMessage();
        this._publishBundleAddToDataLayer(items, cartResponse);

        if (!this.cart) {
          window.location.href = window.routes.cart_url;
          return;
        }

        this.cart.renderContents(cartResponse);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'build-your-bundle',
          cartData: cartResponse,
        });
      })
      .catch((error) => {
        console.error(error);
        this._setBundleAtcError(window.cartStrings?.error);
      })
      .finally(() => {
        bundleAtc.classList.remove('loading');
        bundleAtc.removeAttribute('aria-disabled');
        if (spinner) spinner.classList.add('hidden');
        this._handleButtonLabel();
      });
  }

  /* Remove from bundle */

  _onBundleRemoveClick(evt) {
    const removeBtn = evt.target.closest(this._selectors.bundleRemoveSelection);
    if (!removeBtn || !this.contains(removeBtn)) return;

    evt.preventDefault();
    evt.stopPropagation();

    const blockId = removeBtn.dataset.bundleBlockId;
    const variantId = removeBtn.dataset.bundleVariantId;
    if (!blockId || !variantId) return;

    this._removeBundleSelection(blockId, variantId);
  }

  _removeBundleSelection(blockId, variantId) {
    const step = this._stepConfig.find((s) => s.blockId === String(blockId));
    const selections = [...(this._selections.get(blockId) || [])];
    const prevCount = selections.length;
    const nextSelections = selections.filter((selection) => selection.variantId !== variantId);
    this._trackOptionalStepRegression(step, prevCount, nextSelections.length);
    this._selections.set(blockId, nextSelections);
    this._syncBundle();
  }

  _bundleRemoveIconMarkup() {
    return '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-bundle-close" viewBox="5.5 5.5 13 13" aria-hidden="true"><path stroke="currentColor" stroke-width="1.65" stroke-linecap="round" d="M7.35 7.35 16.65 16.65M16.65 7.35 7.35 16.65"/></svg>';
  }

  _createBundleRemoveButton(blockId, variantId) {
    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.setAttribute('js-bundle-remove-selection', '');
    removeButton.className = 'section-byb__bundle-remove';
    removeButton.dataset.bundleBlockId = blockId;
    removeButton.dataset.bundleVariantId = variantId;
    removeButton.setAttribute('aria-label', 'Remove from bundle');
    removeButton.innerHTML = this._bundleRemoveIconMarkup();
    return removeButton;
  }

  _getProductImageClone(card) {
    if (!card) return null;
    const productImage = card.querySelector('.card__media img');
    if (!productImage) return null;
    const clone = productImage.cloneNode(true);
    if (clone instanceof HTMLImageElement) {
      clone.removeAttribute('loading');
    }
    return clone;
  }

  /* Sync UI */

  _syncButtonLabelsFromState() {
    this.querySelectorAll(this._selectors.quickAddSelectorBundle).forEach((addButton) => {
      const blockId = addButton.dataset.bundleBlockId;
      const variantId = addButton.dataset.variantId;
      if (!blockId || !variantId) return;
      const selections = this._selections.get(blockId) || [];
      const isSelected = selections.some((selection) => selection.variantId === variantId);
      this._handleButtonAddedLabel(addButton, isSelected);
    });
  }

  _handleButtonAddedLabel(button, isAdded) {
    const label = button.querySelector(this._selectors.addLabel);
    if (!label) return;

    if (isAdded) {
      label.textContent = 'Remove From Bundle';
    } else if (!button.disabled) {
      label.textContent = 'Add To Bundle';
    }
  }

  _syncPlaceholders() {
    this.querySelectorAll(`${this._selectors.bundlePlaceholder}[data-bundle-block-id]`).forEach((placeholder) => {
      const blockId = placeholder.dataset.bundleBlockId;
      const slotIndex = parseInt(placeholder.dataset.bundleSlotIndex || '1', 10) - 1;
      if (!blockId || isNaN(slotIndex)) return;

      const selections = this._selections.get(blockId) || [];
      const selection = selections[slotIndex];
      placeholder.innerHTML = '';

      if (selection) {
        // Prefer the per-product bundle image (bundle_builder.thumbnail metafield);
        // fall back to a clone of the card image when it isn't set.
        let slotImage = null;
        if (selection.bundleImage) {
          slotImage = document.createElement('img');
          slotImage.src = selection.bundleImage;
          slotImage.alt = selection.title || '';
        } else {
          slotImage = this._getProductImageClone(selection.card);
        }
        if (slotImage) placeholder.appendChild(slotImage);
      }

      // Filled slot → clicking it jumps back to that product in its step (opt-in).
      if (selection && this._slotClickEnabled) {
        placeholder.classList.add('has-product');
        placeholder.setAttribute('role', 'button');
        placeholder.setAttribute('tabindex', '0');
        placeholder.setAttribute('aria-label', `Go to ${selection.title || 'product'}`);
      } else {
        placeholder.classList.remove('has-product');
        placeholder.removeAttribute('role');
        placeholder.removeAttribute('tabindex');
        placeholder.removeAttribute('aria-label');
      }
    });
  }

  _scrollToPlaceholderProduct(placeholder) {
    const blockId = placeholder.dataset.bundleBlockId;
    const slotIndex = parseInt(placeholder.dataset.bundleSlotIndex || '1', 10) - 1;
    const selection = (this._selections.get(blockId) || [])[slotIndex];
    const target = selection && selection.card;
    if (!target) return;

    this._expandBundleAccordion(blockId, { closeSiblings: false });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this._scrollBundleStepHeaderToTop(target));
    });
  }

  _syncCardRemoveOnMedia() {
    this.querySelectorAll('.bundle-card').forEach((bundleCard) => {
      const addBtn = bundleCard.querySelector(this._selectors.quickAddSelectorBundle);
      const blockId = addBtn && addBtn.dataset.bundleBlockId;
      const variantId = addBtn && addBtn.dataset.variantId;
      const media = bundleCard.querySelector('.card__media');
      if (!media || !blockId || !variantId) return;

      const selections = this._selections.get(blockId) || [];
      const isSelected = selections.some((selection) => selection.variantId === variantId);
      const existingRemoveBtn = media.querySelector(this._selectors.bundleRemoveSelection);

      if (!isSelected) {
        if (existingRemoveBtn) existingRemoveBtn.remove();
        return;
      }

      if (!existingRemoveBtn) {
        media.appendChild(this._createBundleRemoveButton(blockId, variantId));
      } else {
        existingRemoveBtn.dataset.bundleBlockId = blockId;
        existingRemoveBtn.dataset.bundleVariantId = variantId;
      }
    });
  }

  _isVariantSelectedInOtherStep(variantId, blockId) {
    const vid = String(variantId);
    const bid = String(blockId);
    return this._stepConfig.some((s) => {
      if (String(s.blockId) === bid) return false;
      return (this._selections.get(s.blockId) || []).some((sel) => String(sel.variantId) === vid);
    });
  }

  _syncCrossStepProductVisibility() {
    this.querySelectorAll(this._selectors.productCardBundle).forEach((cardRoot) => {
      const bundleCard = cardRoot.closest('.bundle-card');
      const addBtn = cardRoot.querySelector(this._selectors.quickAddSelectorBundle);
      if (!bundleCard || !addBtn) return;
      const blockId = addBtn.dataset.bundleBlockId;
      const variantId = addBtn.dataset.variantId;
      if (!blockId || !variantId) return;
      const hideCard = this._isVariantSelectedInOtherStep(variantId, blockId);
      bundleCard.toggleAttribute('hidden', hideCard);
      bundleCard.setAttribute('aria-hidden', hideCard ? 'true' : 'false');
    });
  }

  _syncCardBackgrounds() {
    this.querySelectorAll('.bundle-card').forEach((bundleCard) => {
      const addButton = bundleCard.querySelector(this._selectors.quickAddSelectorBundle);
      const blockId = addButton && addButton.dataset.bundleBlockId;
      if (!blockId) return;

      const selectionColor = bundleCard.dataset.bundleSelectionColor || '';
      const variantId = addButton.dataset.variantId;
      const selections = this._selections.get(blockId) || [];
      const isSelected = variantId && selections.some((selection) => selection.variantId === variantId);

      if (isSelected) {
        bundleCard.classList.add('is-bundle-selected');
        bundleCard.style.backgroundColor = selectionColor;
      } else {
        bundleCard.classList.remove('is-bundle-selected');
        bundleCard.style.backgroundColor = '';
      }
    });
  }

  /* Progress */

  _syncProgress() {
    // One column per product slot: filled (colour + check) when that slot holds
    // a selection. A step of size 2 owns two slots, indexed 1 and 2.
    this.querySelectorAll(this._selectors.progressStep).forEach((el) => {
      const blockId = el.dataset.bundleBlockId;
      const slotIndex = parseInt(el.dataset.bundleSlotIndex || '1', 10) - 1;
      const selections = this._selections.get(blockId) || [];
      const filled = Boolean(selections[slotIndex]);

      el.classList.toggle('is-bundle-step-complete', filled);

      if (filled) {
        el.style.setProperty('--bundle-progress-fill', el.dataset.bundleLineColor || '');
      } else {
        el.style.removeProperty('--bundle-progress-fill');
      }
    });
  }

  /* Accordion status */

  _syncAccordionStatus() {
    this._stepConfig.forEach((step) => {
      const selections = this._selections.get(step.blockId) || [];
      const accordionItem = this._findElementByBundleBlockId(this._selectors.accordionItem, step.blockId);
      if (!accordionItem) return;

      const status = accordionItem.querySelector(this._selectors.stepStatus);
      const label = accordionItem.querySelector(this._selectors.stepStatusLabel);
      const selectedCount = selections.length;
      const slotsRequired = step.selectionSize;

      if (label) {
        label.textContent = selectedCount + ' of ' + slotsRequired + ' selected';
      }

      if (status) {
        if (this._isStepVisualComplete(step)) {
          status.classList.add('is-bundle-step-complete');
        } else {
          status.classList.remove('is-bundle-step-complete');
        }
      }
    });
  }

  /* Totals */

  _formatMoney(cents) {
    const format = this.dataset.moneyFormat;
    if (typeof theme !== 'undefined' && theme.utils && typeof theme.utils.formatMoney === 'function') {
      return theme.utils.formatMoney(cents, format);
    }
    return (cents / 100).toFixed(2);
  }

  _syncTotals() {
    let totalCents = 0;
    let itemCount = 0;

    this._selections.forEach((selections) => {
      selections.forEach((selection) => {
        totalCents += selection.priceCents;
        itemCount += 1;
      });
    });

    const tierRatio = this._getTierDiscountRatio();
    const sitewideRatio = this._getSitewideRatio();
    const fullRatio = this._getBundleDiscountRatio();

    const afterBundleCents = Math.round(totalCents * (1 - tierRatio));
    const finalCents = Math.round(totalCents * (1 - fullRatio));

    const hasBundleDiscount = tierRatio > 0;
    const hasSitewide = sitewideRatio > 0;
    const anyDiscount = fullRatio > 0;

    // Price row: [RRP] [after bundle discount] final
    //   - RRP strikethrough whenever any discount applies
    //   - middle (after bundle discount) only when the sitewide promo stacks on
    //     top of a bundle discount; otherwise it would equal RRP or the final.
    const showOriginal = totalCents > 0 && anyDiscount;
    const showMid = totalCents > 0 && hasSitewide && hasBundleDiscount;

    const finalDisplay = this._formatMoney(anyDiscount ? finalCents : totalCents);
    const originalDisplay = this._formatMoney(totalCents);
    const midDisplay = this._formatMoney(afterBundleCents);

    // Each total can render in more than one place (the main summary and sticky bar).
    this.querySelectorAll(this._selectors.bundleCount).forEach((countEl) => {
      countEl.textContent = String(itemCount);
    });

    this.querySelectorAll(this._selectors.bundleDiscountedPrice).forEach((el) => {
      el.innerHTML = finalDisplay;
    });

    this.querySelectorAll(this._selectors.bundleOriginalPrice).forEach((el) => {
      el.hidden = !showOriginal;
      el.innerHTML = showOriginal ? originalDisplay : '';
    });

    this.querySelectorAll(this._selectors.bundleMidPrice).forEach((el) => {
      el.hidden = !showMid;
      el.innerHTML = showMid ? midDisplay : '';
    });
  }

  _getTotalSelectedCount() {
    let count = 0;
    this._selections.forEach((selections) => {
      count += selections.length;
    });
    return count;
  }

  _isStartBuildingMode() {
    return this._getTotalSelectedCount() === 0;
  }

  _handleButtonLabel() {
    // Empty bundle → "Start Building" (always clickable, jumps to Step 1).
    // Building → the ATC label, disabled until every required step is met.
    const startMode = this._isStartBuildingMode();
    const label = startMode ? this._startLabel : this._atcLabel;
    const disabled = startMode ? false : !this._stepsCompleteFlags().every(Boolean);

    this.querySelectorAll(this._selectors.quickAddBundleAtc).forEach((bundleAtc) => {
      bundleAtc.disabled = disabled;
      const labelEl = bundleAtc.querySelector(this._selectors.bundleAtcLabel);
      if (labelEl && label) labelEl.textContent = label;
    });
  }

  _goToFirstStep() {
    const firstStep = this._stepConfig[0];
    if (!firstStep) return;
    this._expandBundleAccordion(firstStep.blockId, { closeSiblings: true });
    this._scrollBundleStepProductsIntoView(firstStep.blockId);
  }

  _stepsCompleteFlags() {
    return this._stepConfig.map((step) => this._isStepRequirementMet(step));
  }

  /* DOM & accordion */

  _findElementByBundleBlockId(baseSelector, blockId) {
    const id = String(blockId);
    return Array.from(this.querySelectorAll(baseSelector)).find(
      (element) => element.dataset.bundleBlockId === id
    );
  }

  _getBundleAccordion(blockId) {
    return this._findElementByBundleBlockId(this._selectors.accordionItem, blockId);
  }

  _getBundleHeaderHeightPx() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
    if (raw.endsWith('px')) {
      const n = parseFloat(raw);
      if (Number.isFinite(n)) return n;
    }
    const headerEl = document.querySelector('.shopify-section-header-sticky, .shopify-section-header');
    return headerEl && headerEl.offsetHeight ? headerEl.offsetHeight : 0;
  }

  _getBundleStickyOffsetPx(scrollingUp) {
    // "on-scroll-up" headers hide when scrolling down, so only reserve their height
    // when the header will actually be on screen (always-sticky, or scrolling up reveals it).
    const stickyHeader = document.querySelector('sticky-header');
    const type = stickyHeader ? stickyHeader.dataset.stickyType : null;
    const alwaysVisible = type === 'always' || type === 'reduce-logo-size';
    if (alwaysVisible || scrollingUp) return this._getBundleHeaderHeightPx() + 12;
    return 12;
  }

  _scrollBundleStepHeaderToTop(element, settleInstant = false) {
    if (!element) return;
    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const root = document.scrollingElement || document.documentElement;
    const rawTop = root.scrollTop + element.getBoundingClientRect().top;
    const scrollingUp = rawTop < root.scrollTop;
    const top = Math.max(0, rawTop - this._getBundleStickyOffsetPx(scrollingUp));
    const behavior = prefersReduced || settleInstant ? 'auto' : 'smooth';
    try {
      root.scrollTo({ top, behavior });
    } catch (_) {
      root.scrollTop = top;
    }
  }

  _getOpenAccordionBlockId() {
    let lastOpen = null;
    for (const step of this._stepConfig) {
      const accordionItem = this._getBundleAccordion(step.blockId);
      if (!accordionItem) continue;
      const header = accordionItem.querySelector(this._selectors.accordionHeader);
      if (header && header.getAttribute('aria-expanded') === 'true') {
        lastOpen = step.blockId;
      }
    }
    return lastOpen;
  }

  _resolveProgressActiveBlockId() {
    const failIdx = this._stepConfig.findIndex((s) => !this._stepPassesForProgress(s));
    const firstBad = failIdx === -1 ? this._stepConfig.length : failIdx;
    const openId = this._getOpenAccordionBlockId();

    if (openId) {
      const openIdx = this._stepConfig.findIndex((s) => String(s.blockId) === String(openId));
      if (openIdx >= 0 && this._stepsBeforeIndexAllPass(openIdx)) {
        return openId;
      }
    }

    if (firstBad < this._stepConfig.length) {
      return this._stepConfig[firstBad].blockId;
    }

    const last = this._stepConfig[this._stepConfig.length - 1];
    return last ? last.blockId : null;
  }

  _collapseBundleAccordion(blockId) {
    const accordionItem = this._getBundleAccordion(blockId);
    if (!accordionItem) return;
    const header = accordionItem.querySelector(this._selectors.accordionHeader);
    if (!header || header.getAttribute('aria-expanded') === 'false') return;
    const controlsId = header.getAttribute('aria-controls');
    const accordionContent = controlsId ? document.getElementById(controlsId) : null;
    if (!accordionContent) return;
    header.setAttribute('aria-expanded', 'false');
    accordionContent.setAttribute('aria-hidden', 'true');
  }

  _expandBundleAccordion(blockId, options = {}) {
    const closeSiblings = options.closeSiblings !== false;
    const id = String(blockId);
    const accordionItem = this._getBundleAccordion(id);
    if (!accordionItem) return false;

    const header = accordionItem.querySelector(this._selectors.accordionHeader);
    if (!header) return false;

    const controlsId = header.getAttribute('aria-controls');
    const accordionContent = controlsId ? document.getElementById(controlsId) : null;
    if (!accordionContent) return false;

    if (closeSiblings) {
      this._stepConfig.forEach((step) => {
        if (step.blockId !== id) {
          this._collapseBundleAccordion(step.blockId);
        }
      });
    }

    if (header.getAttribute('aria-expanded') !== 'true') {
      header.setAttribute('aria-expanded', 'true');
      if (typeof theme !== 'undefined' && theme.utils && typeof theme.utils.prepareTransition === 'function') {
        theme.utils.prepareTransition(accordionContent);
      }
      accordionContent.setAttribute('aria-hidden', 'false');
    }

    return true;
  }

  _scrollBundleStepProductsIntoView(blockId) {
    const accordionItem = this._getBundleAccordion(blockId);
    if (!accordionItem) return;
    const header = accordionItem.querySelector(this._selectors.accordionHeader);
    const target = header || accordionItem;

    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => this._scrollBundleStepHeaderToTop(target), 50);
        if (!prefersReduced) {
          setTimeout(() => this._scrollBundleStepHeaderToTop(target, true), 320);
        }
      });
    });
  }

  /* Advance to next step when a step completes */

  _handleStepComplete() {
    const flagsNow = this._stepConfig.map((s) =>
      s.mandatory ? this._isStepRequirementMet(s) : this._isStepVisualComplete(s)
    );
    const flagsPrev = this._prevStepAdvanceFlags;
    this._prevStepAdvanceFlags = flagsNow;
    if (!flagsPrev) return;

    const completedStepIndex = flagsNow.findIndex(
      (isCompleteNow, stepIndex) => isCompleteNow && !flagsPrev[stepIndex]
    );
    const nextStep =
      completedStepIndex >= 0 ? this._stepConfig[completedStepIndex + 1] : null;
    if (nextStep) {
      this._expandBundleAccordion(nextStep.blockId, { closeSiblings: false });
      this._scrollBundleStepProductsIntoView(nextStep.blockId);
    }
  }

  /* Sticky bar */

  _initStickyBar() {
    this._stickyBar = this.querySelector(this._selectors.bundleSticky);
    if (!this._stickyBar) return;

    const toggle = this._stickyBar.querySelector(this._selectors.bundleStickyToggle);
    if (toggle) {
      toggle.addEventListener('click', () => this._toggleStickyCollapse());
    }

    // Reveal the bar only once the main add-to-cart has scrolled up out of view —
    // the original CTA owns the first screen.
    const sentinel = this.querySelector(this._selectors.stickySentinel);
    if (!sentinel || !('IntersectionObserver' in window)) return;

    // Drop the no-JS guard so the CSS resting state (off-screen, invisible) can transition.
    this._stickyBar.hidden = false;

    this._stickyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this._setStickyVisible(scrolledPast);
        });
      },
      { threshold: 0 }
    );
    this._stickyObserver.observe(sentinel);
  }

  _setStickyVisible(visible) {
    if (!this._stickyBar) return;
    this._stickyBar.classList.toggle('section-byb__sticky--visible', visible);
  }

  _toggleStickyCollapse() {
    if (!this._stickyBar) return;
    const collapsed = this._stickyBar.classList.toggle('section-byb__sticky--collapsed');
    const toggle = this._stickyBar.querySelector(this._selectors.bundleStickyToggle);
    if (toggle) {
      toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      toggle.setAttribute('aria-label', collapsed ? 'Expand bundle preview' : 'Collapse bundle preview');
    }
  }

  /* Sync */

  _syncBundle() {
    this._syncButtonLabelsFromState();
    this._syncPlaceholders();
    this._syncCrossStepProductVisibility();
    this._syncCardBackgrounds();
    this._syncCardRemoveOnMedia();
    this._handleStepComplete();
    this._syncProgress();
    this._syncAccordionStatus();
    this._syncTotals();
    this._handleButtonLabel();
    this._updateTierMessage();
    this._syncCrawlableSelectionSummary();
    this._publishBundleBuilderComplete();
    this._writeUrl();
  }
}

customElements.define('s-bundle-section', BuildYourBundle);
